'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SalesTaxCalculator() {
  const t = useTranslations("tools.salesTaxCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    beforeTax: '',
    taxRate: '',
    afterTax: ''
  });
  const [results, setResults] = useState<{
    beforeTax: number;
    taxRate: number;
    afterTax: number;
    taxAmount: number;
  } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      maxWidth: '800px',
      margin: '0 auto',
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    toolContainer: {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const,
    },
    toolHeaderTitle: {
      margin: 0,
      fontWeight: 700,
    },
    calculatorForm: {
      padding: '2rem',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      fontWeight: 600,
      color: colors.textPrimary,
      fontSize: '0.95rem',
    },
    input: {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    inputPrefix: {
      position: 'relative' as const,
    },
    inputSuffix: {
      position: 'relative' as const,
    },
    prefixSymbol: {
      position: 'absolute' as const,
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    suffixSymbol: {
      position: 'absolute' as const,
      right: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#64748b',
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    errorMessage: {
      background: '#fef2f2',
      color: '#dc2626',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #dc2626',
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem',
    },
    calculateBtn: {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    clearBtn: {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    results: {
      padding: '2rem',
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
    },
    resultsTitle: {
      margin: '0 0 1.5rem 0',
      color: colors.textPrimary,
      textAlign: 'center' as const,
    },
    resultGrid: {
      display: 'grid',
      gap: '1rem',
      marginBottom: '1.5rem',
    },
    resultItem: {
      background: 'white',
      padding: '1.25rem',
      borderRadius: '0.75rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      borderLeft: '4px solid #2563eb',
    },
    resultItemHighlight: {
      background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
      borderLeft: '4px solid #f59e0b',
    },
    resultLabel: {
      display: 'block',
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '0.5rem',
      fontWeight: 600,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px',
    },
    resultValue: {
      display: 'block',
      fontWeight: 700,
      color: colors.textPrimary,
    },
    resultValueHighlight: {
      color: '#f59e0b',
    },
    breakdown: {
      background: 'white',
      padding: '1.5rem',
      borderRadius: '0.75rem',
      marginTop: '1rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    breakdownTitle: {
      margin: '0 0 1rem 0',
      color: colors.textPrimary,
    },
    breakdownRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem 0',
      borderBottom: '1px solid #e2e8f0',
    },
    breakdownRowLast: {
      borderBottom: 'none',
      fontWeight: 700,
      color: '#2563eb',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    h3: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.75rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      marginLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.5rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    strongText: {
      fontWeight: 600,
      color: colors.textPrimary,
    },
    faqItem: {
      marginBottom: '1.5rem',
      paddingBottom: '1.5rem',
      borderBottom: `1px solid ${colors.glassBorder}`,
    },
    reference: {
      fontSize: '0.9rem',
      color: colors.textSecondary,
      marginBottom: '0.5rem',
    },
    link: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const beforeTax = parseFloat(inputs.beforeTax) || 0;
    const taxRate = parseFloat(inputs.taxRate) || 0;
    const afterTax = parseFloat(inputs.afterTax) || 0;

    let calculatedBeforeTax: number, calculatedTaxRate: number, calculatedAfterTax: number, taxAmount: number;

    const filledFields = [beforeTax > 0, taxRate > 0, afterTax > 0].filter(Boolean).length;

    if (filledFields < 2) {
      setError(t('errorMinimumFields'));
      return;
    }

    if (beforeTax > 0 && taxRate > 0 && afterTax === 0) {
      calculatedBeforeTax = beforeTax;
      calculatedTaxRate = taxRate;
      taxAmount = beforeTax * (taxRate / 100);
      calculatedAfterTax = beforeTax + taxAmount;
    } else if (beforeTax > 0 && afterTax > 0 && taxRate === 0) {
      if (afterTax <= beforeTax) {
        setError(t('errorAfterTaxGreater'));
        return;
      }
      calculatedBeforeTax = beforeTax;
      calculatedAfterTax = afterTax;
      taxAmount = afterTax - beforeTax;
      calculatedTaxRate = (taxAmount / beforeTax) * 100;
    } else if (taxRate > 0 && afterTax > 0 && beforeTax === 0) {
      calculatedTaxRate = taxRate;
      calculatedAfterTax = afterTax;
      calculatedBeforeTax = afterTax / (1 + taxRate / 100);
      taxAmount = calculatedAfterTax - calculatedBeforeTax;
    } else if (beforeTax > 0 && taxRate > 0 && afterTax > 0) {
      const expectedAfterTax = beforeTax * (1 + taxRate / 100);
      if (Math.abs(expectedAfterTax - afterTax) > 0.01) {
        setError(t('errorInconsistentValues'));
        return;
      }
      calculatedBeforeTax = beforeTax;
      calculatedTaxRate = taxRate;
      calculatedAfterTax = afterTax;
      taxAmount = afterTax - beforeTax;
    } else {
      setError(t('errorInvalidCombination'));
      return;
    }

    if (calculatedTaxRate < 0 || calculatedTaxRate > 100) {
      setError(t('errorTaxRateRange'));
      return;
    }

    setResults({
      beforeTax: calculatedBeforeTax,
      taxRate: calculatedTaxRate,
      afterTax: calculatedAfterTax,
      taxAmount: taxAmount
    });
  };

  const handleClear = () => {
    setInputs({ beforeTax: '', taxRate: '', afterTax: '' });
    setResults(null);
    setError('');
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const formatPercent = (value: number) => {
    return value.toFixed(2) + '%';
  };

  const containerStyle = getResponsiveStyle(
    baseStyles.container,
    { padding: '1rem' },
    { padding: '2rem' },
    { padding: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    baseStyles.h1,
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    baseStyles.summaryText,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    baseStyles.calculatorTool,
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem' },
    { padding: '3rem 2rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    baseStyles.toolContainer,
    { maxWidth: '100%' },
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderTitleStyle = getResponsiveStyle(
    baseStyles.toolHeaderTitle,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.8rem' }
  );

  const inputStyle = getResponsiveStyle(
    baseStyles.input,
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1.25rem' },
    { padding: '1rem 1.5rem' }
  );

  const inputPrefixStyle = {
    ...inputStyle,
    paddingLeft: screenSize === 'mobile' ? '2rem' : screenSize === 'tablet' ? '2.25rem' : '2.5rem',
  };

  const inputSuffixStyle = {
    ...inputStyle,
    paddingRight: screenSize === 'mobile' ? '2.5rem' : screenSize === 'tablet' ? '2.75rem' : '3rem',
  };

  const calculateBtnStyle = getResponsiveStyle(
    baseStyles.calculateBtn,
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const clearBtnStyle = getResponsiveStyle(
    baseStyles.clearBtn,
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    baseStyles.resultGrid,
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultValueStyle = getResponsiveStyle(
    baseStyles.resultValue,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    baseStyles.glassCard,
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    baseStyles.h2,
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    baseStyles.h3,
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    baseStyles.paragraph,
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    baseStyles.buttonGroup,
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const breakdownTitleStyle = getResponsiveStyle(
    baseStyles.breakdownTitle,
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.3rem' }
  );

  const resultsTitleStyle = getResponsiveStyle(
    baseStyles.resultsTitle,
    { fontSize: '1.3rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  return (
    <div style={containerStyle}>
      <header style={baseStyles.header}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={baseStyles.toolHeader}>
            <h2 style={toolHeaderTitleStyle}>{t('toolTitle')}</h2>
          </div>

          <form style={baseStyles.calculatorForm} onSubmit={handleCalculate}>
            <div style={baseStyles.formGroup}>
              <label style={baseStyles.label} htmlFor="before-tax">{t('beforeTaxLabel')}</label>
              <div style={baseStyles.inputPrefix}>
                <span style={baseStyles.prefixSymbol}>$</span>
                <input
                  type="number"
                  id="before-tax"
                  step="0.01"
                  min="0"
                  placeholder={t('beforeTaxPlaceholder')}
                  value={inputs.beforeTax}
                  onChange={(e) => setInputs({...inputs, beforeTax: e.target.value})}
                  style={inputPrefixStyle}
                />
              </div>
            </div>

            <div style={baseStyles.formGroup}>
              <label style={baseStyles.label} htmlFor="tax-rate">{t('taxRateLabel')}</label>
              <div style={baseStyles.inputSuffix}>
                <input
                  type="number"
                  id="tax-rate"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder={t('taxRatePlaceholder')}
                  value={inputs.taxRate}
                  onChange={(e) => setInputs({...inputs, taxRate: e.target.value})}
                  style={inputSuffixStyle}
                />
                <span style={baseStyles.suffixSymbol}>%</span>
              </div>
            </div>

            <div style={baseStyles.formGroup}>
              <label style={baseStyles.label} htmlFor="after-tax">{t('afterTaxLabel')}</label>
              <div style={baseStyles.inputPrefix}>
                <span style={baseStyles.prefixSymbol}>$</span>
                <input
                  type="number"
                  id="after-tax"
                  step="0.01"
                  min="0"
                  placeholder={t('afterTaxPlaceholder')}
                  value={inputs.afterTax}
                  onChange={(e) => setInputs({...inputs, afterTax: e.target.value})}
                  style={inputPrefixStyle}
                />
              </div>
            </div>

            {error && (
              <div style={baseStyles.errorMessage}>{error}</div>
            )}

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateBtnStyle}>{t('calculateButton')}</button>
              <button type="button" style={clearBtnStyle} onClick={handleClear}>{t('clearButton')}</button>
            </div>
          </form>

          {results && (
            <div style={baseStyles.results}>
              <h3 style={resultsTitleStyle}>{t('resultsTitle')}</h3>

              <div style={resultGridStyle}>
                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('resultBeforeTax')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.beforeTax)}</span>
                </div>

                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('resultTaxAmount')}</span>
                  <span style={resultValueStyle}>{formatCurrency(results.taxAmount)}</span>
                </div>

                <div style={{...baseStyles.resultItem, ...baseStyles.resultItemHighlight}}>
                  <span style={baseStyles.resultLabel}>{t('resultAfterTax')}</span>
                  <span style={{...resultValueStyle, ...baseStyles.resultValueHighlight}}>{formatCurrency(results.afterTax)}</span>
                </div>
              </div>

              <div style={baseStyles.breakdown}>
                <h4 style={breakdownTitleStyle}>{t('breakdownTitle')}</h4>
                <div style={baseStyles.breakdownRow}>
                  <span>{t('breakdownOriginal')}</span>
                  <span>{formatCurrency(results.beforeTax)}</span>
                </div>
                <div style={baseStyles.breakdownRow}>
                  <span>{t('breakdownRate')}</span>
                  <span>{formatPercent(results.taxRate)}</span>
                </div>
                <div style={baseStyles.breakdownRow}>
                  <span>{t('breakdownTax')}</span>
                  <span>{formatCurrency(results.taxAmount)}</span>
                </div>
                <div style={{...baseStyles.breakdownRow, ...baseStyles.breakdownRowLast}}>
                  <span>{t('breakdownTotal')}</span>
                  <span>{formatCurrency(results.afterTax)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={baseStyles.listItem}><span style={baseStyles.strongText}>{t('step1Title')}</span> {t('step1Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strongText}>{t('step2Title')}</span> {t('step2Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strongText}>{t('step3Title')}</span> {t('step3Description')}</li>
          <li style={baseStyles.listItem}><span style={baseStyles.strongText}>{t('step4Title')}</span> {t('step4Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insight1Title')}</h3>
        <p style={paragraphStyle}>{t('insight1Description')}</p>

        <h3 style={h3Style}>{t('insight2Title')}</h3>
        <p style={paragraphStyle}>{t('insight2Description')}</p>

        <h3 style={h3Style}>{t('insight3Title')}</h3>
        <p style={paragraphStyle}>{t('insight3Description')}</p>

        <h3 style={h3Style}>{t('insight4Title')}</h3>
        <p style={paragraphStyle}>{t('insight4Description')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detail1Title')}</h3>
        <p style={paragraphStyle}>{t('detail1Description')}</p>

        <h3 style={h3Style}>{t('detail2Title')}</h3>
        <p style={paragraphStyle}>{t('detail2Description')}</p>

        <h3 style={h3Style}>{t('detail3Title')}</h3>
        <p style={paragraphStyle}>{t('detail3Description')}</p>

        <h3 style={h3Style}>{t('detail4Title')}</h3>
        <p style={paragraphStyle}>{t('detail4Description')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={h3Style}>{t('faq1Question')}</h3>
          <p style={paragraphStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={h3Style}>{t('faq2Question')}</h3>
          <p style={paragraphStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={h3Style}>{t('faq3Question')}</h3>
          <p style={paragraphStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={h3Style}>{t('faq4Question')}</h3>
          <p style={paragraphStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={h3Style}>{t('faq5Question')}</h3>
          <p style={paragraphStyle}>{t('faq5Answer')}</p>
        </div>

        <div style={{...baseStyles.faqItem, borderBottom: 'none'}}>
          <h3 style={h3Style}>{t('faq6Question')}</h3>
          <p style={paragraphStyle}>{t('faq6Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={baseStyles.reference}>
          1. <a href="https://www.irs.gov/credits-deductions/individuals/use-the-sales-tax-deduction-calculator" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            {t('reference1')}
          </a>
        </p>
        <p style={baseStyles.reference}>
          2. <a href="https://quickbooks.intuit.com/accounting/sales-tax/calculator/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            {t('reference2')}
          </a>
        </p>
        <p style={baseStyles.reference}>
          3. <a href="https://www.avalara.com/taxrates/en/state-rates.html" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            {t('reference3')}
          </a>
        </p>
        <p style={baseStyles.reference}>
          4. <a href="https://www.taxjar.com/blog/a-practical-guide-to-sales-tax-changes-and-trends-for-2026" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            {t('reference4')}
          </a>
        </p>
        <p style={baseStyles.reference}>
          5. <a href="https://www.calculator.net/sales-tax-calculator.html" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            {t('reference5')}
          </a>
        </p>
        <p style={{...baseStyles.reference, marginBottom: 0}}>
          6. <a href="https://vatcompliance.com/how-to-calculate-sales-tax-in-the-us/" target="_blank" rel="noopener noreferrer" style={baseStyles.link}>
            {t('reference6')}
          </a>
        </p>
      </section>
    </div>
  );
}